import { useState } from 'preact/hooks';
import { rpx } from 'client/lib/rpx-client';
import { useAsyncState } from 'client/lib/hooks';
import { BtnPrimary, BtnSecondary } from '@components/buttons';
import { showToast } from '@components/toaster';
import { showError } from '@components/app-error';
import { IcoCalendar } from '@components/icons';
import { fmtFullTime } from 'shared/dateutil';

type Props = {
  courseId: UUID;
  meetingId: UUID;
  scheduledAt?: Date;
  durationMinutes: number;
  title: string;
};

type Tone = 'neutral' | 'stale' | 'done';

export function CalendarInviteBanner({
  courseId,
  meetingId,
  scheduledAt,
  durationMinutes,
  title,
}: Props) {
  const scheduledKey = scheduledAt ? scheduledAt.toISOString() : '';
  const [status, setStatus] = useAsyncState(
    () => rpx.meetings.getMeetingCalendarInviteStatus({ courseId, meetingId }),
    [courseId, meetingId, scheduledKey, durationMinutes, title],
  );
  const [sending, setSending] = useState(false);

  if (!status || status.state === 'disabled') {
    return null;
  }
  if (status.state === 'never_sent' && status.pendingRecipientCount === 0) {
    return null;
  }

  async function send() {
    setSending(true);
    try {
      const rpc =
        status?.state === 'held'
          ? rpx.meetings.releaseHeldInvites
          : rpx.meetings.sendCalendarInvites;
      const result = await rpc({ courseId, meetingId });
      const fresh = await rpx.meetings.getMeetingCalendarInviteStatus({ courseId, meetingId });
      setStatus(fresh);
      const sentLabel = `${result.sent} invite${result.sent === 1 ? '' : 's'} delivered`;
      const skipParts: string[] = [];
      if (result.skipped > 0) {
        skipParts.push(`${result.skipped} skipped by allowlist`);
      }
      if (result.failures > 0) {
        skipParts.push(`${result.failures} failed`);
      }
      const detail = skipParts.length > 0 ? ` · ${skipParts.join(' · ')}` : '';
      const tone = result.failures > 0 ? 'warn' : 'ok';
      const title =
        result.sent === 0 && result.skipped > 0 && result.failures === 0
          ? 'No invites delivered'
          : 'Calendar invites sent';
      showToast({
        type: tone,
        title,
        message: `${sentLabel}${detail}.`,
      });
    } catch (err) {
      showError(err);
    } finally {
      setSending(false);
    }
  }

  if (status.state === 'held') {
    const count = status.pendingRecipientCount;
    return (
      <Bar tone="stale">
        <Detail>
          <strong>Invites held</strong>
          <span class="opacity-75">
            {' · '}
            release to send the .ics to {count} member{count === 1 ? '' : 's'}
          </span>
        </Detail>
        <BtnPrimary
          disabled={sending || count === 0}
          onClick={send}
          class="rounded-full px-5 text-sm"
        >
          {sending ? 'Sending…' : 'Send invites now'}
        </BtnPrimary>
      </Bar>
    );
  }

  if (status.state === 'never_sent') {
    const count = status.pendingRecipientCount;
    return (
      <Bar tone="neutral">
        <Detail>
          <strong>Calendar invites</strong>
          <span class="opacity-75">
            {' · '}
            {count} member{count === 1 ? '' : 's'} will receive an .ics for this meeting
          </span>
        </Detail>
        <BtnPrimary
          disabled={sending || count === 0}
          onClick={send}
          class="rounded-full px-5 text-sm"
        >
          {sending ? 'Sending…' : 'Send invites'}
        </BtnPrimary>
      </Bar>
    );
  }

  if (status.state === 'sent_stale') {
    const count = status.pendingRecipientCount;
    return (
      <Bar tone="stale">
        <Detail>
          <strong>Call changed</strong>
          <span class="opacity-75">
            {' · '}
            resend updated invites to {count} member{count === 1 ? '' : 's'}
          </span>
        </Detail>
        <BtnPrimary disabled={sending} onClick={send} class="rounded-full px-5 text-sm">
          {sending ? 'Sending…' : 'Resend invites'}
        </BtnPrimary>
      </Bar>
    );
  }

  return (
    <Bar tone="done">
      <Detail>
        <strong>Calendar invites sent</strong>
        <span class="opacity-75">
          {' · '}
          {status.sentMemberCount} member{status.sentMemberCount === 1 ? '' : 's'}
          {status.lastSentAt ? ` on ${fmtFullTime(status.lastSentAt)}` : ''}
        </span>
      </Detail>
      <BtnSecondary disabled={sending} onClick={send} class="rounded-full px-5 text-sm">
        {sending ? 'Sending…' : 'Resend'}
      </BtnSecondary>
    </Bar>
  );
}

function Bar({ tone, children }: { tone: Tone; children: preact.ComponentChildren }) {
  const toneClasses =
    tone === 'neutral'
      ? 'bg-gray-50 border-gray-200 text-gray-800'
      : tone === 'stale'
        ? 'bg-amber-50 border-amber-200 text-amber-900'
        : 'bg-emerald-50/60 border-emerald-200 text-emerald-900';
  const iconClass =
    tone === 'neutral' ? 'text-gray-500' : tone === 'stale' ? 'text-amber-600' : 'text-emerald-600';
  return (
    <div
      class={`flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 border rounded-lg px-4 py-2.5 my-3 ${toneClasses}`}
    >
      <IcoCalendar class={`w-4 h-4 shrink-0 ${iconClass}`} />
      {children}
    </div>
  );
}

function Detail({ children }: { children: preact.ComponentChildren }) {
  return <span class="grow text-sm leading-snug">{children}</span>;
}
